import React, { useState } from "react";
import { useAuth } from "./lib/auth";
import { api, formatErr } from "./lib/api";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [remember, setRemember] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null); // { ok, admins, db, error }

  // v144 — عرض خطأ الدخول بجوجل (بريد غير مسجل / جلسة منتهية)
  React.useEffect(() => {
    const stored = sessionStorage.getItem("tw_google_err");
    if (stored) {
      setErr(stored);
      sessionStorage.removeItem("tw_google_err");
    }
    const onGErr = (e) => {
      setErr(e.detail || "فشل الدخول بحساب Google");
      sessionStorage.removeItem("tw_google_err");
    };
    window.addEventListener("google-auth-error", onGErr);
    return () => window.removeEventListener("google-auth-error", onGErr);
  }, []);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setDiag(null);
    setBusy(true);
    try {
      await login(username.trim(), password, needs2fa ? otp : undefined, remember);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      // Detect 2FA challenge — server returns 401 with {requires_2fa: true}
      if (status === 401 && detail && typeof detail === "object" && detail.requires_2fa) {
        setNeeds2fa(true);
        setErr("");
        setBusy(false);
        return;
      }
      let msg;
      if (status === 401) {
        msg = needs2fa ? "رمز التحقق غير صحيح" : "اسم المستخدم أو كلمة السر خاطئة";
      } else if (status === 422) {
        msg = formatErr(detail) || "بيانات الدخول غير صحيحة";
      } else if (status === 423) {
        msg = formatErr(detail) || "تم قفل الحساب مؤقتاً";
      } else if (status === 429) {
        msg = "محاولات كثيرة جداً. الرجاء الانتظار دقيقة وإعادة المحاولة.";
      } else if (status >= 500) {
        msg = `خطأ في الخادم (${status}). تواصل مع الدعم.`;
      } else if (!e.response) {
        msg = `تعذّر الاتصال بالخادم — تأكد من الإنترنت أو راجع المدير. (${e.message})`;
      } else {
        msg = formatErr(detail) || `خطأ غير متوقع (${status || "?"})`;
      }
      console.error("[login] failed:", { status, detail, message: e.message, code: e.code });
      setErr(msg);
      // On any non-401 failure, automatically probe the backend so the user
      // sees whether the server is reachable and the admin user actually exists.
      if (status !== 401) {
        try {
          const { data } = await api.get("/auth/health");
          setDiag(data);
        } catch (probeErr) {
          setDiag({ ok: false, error: probeErr.message, unreachable: true });
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-card" data-testid="login-card">
        <div className="login-brand">
          <img src="/thawany-logo.png" alt="ثواني — Thawany" className="login-logo-img" data-testid="login-logo" />
        </div>
        <h2 className="login-title">تسجيل الدخول</h2>
        <p className="login-sub">أدخل بيانات حسابك للوصول إلى لوحة التحكم</p>
        <form onSubmit={submit} className="login-form">
          <label className="login-label">اسم المستخدم</label>
          <input
            data-testid="login-username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="admin"
            required
            className="login-input"
          />
          <label className="login-label">كلمة السر</label>
          <input
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            className="login-input"
          />
          {needs2fa && (
            <>
              <label className="login-label">🔐 رمز التحقق بخطوتين (6 أرقام)</label>
              <input
                data-testid="login-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                required
                autoFocus
                className="login-input"
                style={{ letterSpacing: 6, textAlign: "center", fontSize: 18, fontWeight: 700 }}
              />
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: -4 }}>
                افتح تطبيق المُصادقة (Google Authenticator / Authy) واقرأ الرمز الحالي، أو أدخل رمز نسخ احتياطي.
              </div>
            </>
          )}
          <label className="login-label" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" data-testid="login-remember" checked={remember}
              onChange={(e) => setRemember(e.target.checked)} />
            تذكرني على هذا الجهاز
          </label>
          {err && <div className="login-err" data-testid="login-error">{err}</div>}
          {diag && (
            <div data-testid="login-diag" style={{ background: diag.ok ? "var(--success-soft)" : "var(--warn-soft)", color: "var(--brown-deep)", padding: 10, borderRadius: 8, fontSize: 12, marginTop: 8, lineHeight: 1.7 }}>
              {diag.unreachable && (<>🔴 الخادم غير قابل للوصول. تواصل مع الدعم لإعادة تشغيل الخدمة.</>)}
              {!diag.unreachable && diag.ok && diag.admins > 0 && (
                <>✅ الخادم متصل وقاعدة البيانات سليمة ({diag.admins} حساب مدير). إذا استمر الخطأ تأكد من اسم المستخدم وكلمة السر.</>
              )}
              {!diag.unreachable && diag.ok && diag.admins === 0 && (
                <>⚠ الخادم متصل لكن لا يوجد حساب مدير في قاعدة البيانات. تواصل مع الدعم لإعادة تشغيل الخدمة (الإعداد التلقائي ينشئ المدير عند الإقلاع).</>
              )}
              {!diag.ok && !diag.unreachable && (
                <>🔴 خطأ في قاعدة البيانات: {String(diag.error || "غير معروف").slice(0, 120)}</>
              )}
            </div>
          )}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="login-btn"
          >
            {busy ? "جارٍ التحقق..." : "دخول"}
          </button>
        </form>
        {/* v144 — الدخول بحساب Google (للموظفين المسجلين فقط) */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0 10px" }}>
          <div style={{ flex: 1, height: 1, background: "var(--border, #e6d9b8)" }} />
          <span style={{ fontSize: 11, color: "var(--muted)" }}>أو</span>
          <div style={{ flex: 1, height: 1, background: "var(--border, #e6d9b8)" }} />
        </div>
        <button
          type="button"
          data-testid="login-google-btn"
          onClick={googleLogin}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
            gap: 10, padding: "11px 16px", borderRadius: 12, cursor: "pointer",
            background: "#fff", border: "1.5px solid var(--border, #e6d9b8)",
            fontWeight: 700, fontSize: 14, color: "#3c4043",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          الدخول بحساب Google
        </button>
        <div style={{ fontSize: 10.5, color: "var(--muted)", textAlign: "center", marginTop: 6, lineHeight: 1.7 }}>
          متاح فقط للموظفين المسجلين — بريدك في Google يجب أن يطابق بريدك في النظام.
        </div>
        <div className="login-hint">
          لا تملك حساباً؟ تواصل مع المدير لإنشائه.
        </div>
      </div>
    </div>
  );
}
